'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import routeDirectory from '~/navigation/routes'

/**
 * Fullscreen ("monitor") mode — ENG-10448.
 *
 * Lets a dashboard be left up on a wall display or second monitor: hides the side nav,
 * top bar and assistant bar, and keeps widgets polling while the tab is unfocused
 * (see `useEffectiveWindowFocus` in `~/hooks/useWindowFocus`).
 *
 * State is ephemeral and per-tab — deliberately not Redux and not localStorage. The URL
 * carries it so the mode is shareable/bookmarkable for a kiosk and never outlives an
 * ordinary visit to `/dashboard`.
 *
 * ## Why the URL is mirrored rather than driven
 *
 * The obvious implementation — `router.replace()` and let `useSearchParams()` flow back
 * down — did not work on deployed builds: clicking the toggle left the URL untouched and
 * nothing happened, while loading `?fullscreen=true` directly worked fine. An App Router
 * soft navigation can be dropped silently (a redirect or a failed RSC fetch for the new
 * URL will do it), and when it is, there is no error and no state change to react to.
 *
 * So the live state is React state, flipped synchronously on click, and the URL is kept in
 * step with `history.replaceState`. The mode can no longer fail to toggle, the URL stays
 * copyable, and a fresh load still seeds from the param. Reading the current query from
 * `window.location` rather than `useSearchParams()` also preserves params written by other
 * `replaceState` callers (e.g. `BackendBootstrap`'s `?backend=`), which Next's hook does
 * not observe.
 */
export const FULLSCREEN_QUERY_PARAM = 'fullscreen'

type FullscreenModeContextValue = {
  isFullscreen: boolean
  setFullscreen: (next: boolean) => void
}

const FullscreenModeContext = createContext<FullscreenModeContextValue>({
  isFullscreen: false,
  setFullscreen: () => {}
})

/** Trailing slashes are on (`next.config.js`), but normalize so the check can't drift. */
const stripTrailingSlash = (path: string) => path.replace(/\/+$/, '')

const DASHBOARD_PATH = stripTrailingSlash(routeDirectory.dashboard.root)

/**
 * Fullscreen is scoped to the dashboard route on purpose: the only affordance for leaving
 * it lives in the dashboard toolbar, so honouring the mode elsewhere would strand a user
 * on a page with no visible chrome.
 */
function useIsDashboardRoute(): boolean {
  const pathname = usePathname()

  return stripTrailingSlash(pathname ?? '') === DASHBOARD_PATH
}

/** Writes the flag into the address bar without asking the router to navigate. */
function mirrorToUrl(next: boolean) {
  if (typeof window === 'undefined') return

  const url = new URL(window.location.href)
  if (next) url.searchParams.set(FULLSCREEN_QUERY_PARAM, 'true')
  else url.searchParams.delete(FULLSCREEN_QUERY_PARAM)

  window.history.replaceState(null, '', url.toString())
}

/**
 * Owns the live flag and shares it by context, so leaf hooks like `usePolling` and
 * `useNowEffect` can consume it without every one of their ~60 call sites (and their
 * tests) gaining a dependency on Next's router context.
 *
 * Mount high enough to sit above `DataManagementProvider` — the global vehicles/sites
 * pollers live there, and the dashboard's VehicleStatus widget depends on them.
 */
export function FullscreenModeProvider({ children }: PropsWithChildren) {
  const onDashboard = useIsDashboardRoute()
  const searchParams = useSearchParams()
  const paramActive = searchParams?.get(FULLSCREEN_QUERY_PARAM) === 'true'

  const [active, setActive] = useState(paramActive)

  // Seeds from the URL on load and follows real navigations (back/forward, a pasted
  // kiosk link). Toggling goes through `setFullscreen` and does not round-trip here.
  useEffect(() => {
    setActive(paramActive)
  }, [paramActive])

  const setFullscreen = useCallback((next: boolean) => {
    setActive(next)
    mirrorToUrl(next)
  }, [])

  // Leaving the dashboard drops the mode even if the flag is somehow still set.
  const isFullscreen = onDashboard && active

  const value = useMemo(() => ({ isFullscreen, setFullscreen }), [isFullscreen, setFullscreen])

  return <FullscreenModeContext.Provider value={value}>{children}</FullscreenModeContext.Provider>
}

/** `false` outside a provider, so consumers are safe to call from anywhere. */
export function useIsFullscreenMode(): boolean {
  return useContext(FullscreenModeContext).isFullscreen
}

export type FullscreenModeControls = {
  isFullscreen: boolean
  enter: () => void
  exit: () => void
  toggle: () => void
}

export function useFullscreenModeControls(): FullscreenModeControls {
  const { isFullscreen, setFullscreen } = useContext(FullscreenModeContext)

  const enter = useCallback(() => setFullscreen(true), [setFullscreen])
  const exit = useCallback(() => setFullscreen(false), [setFullscreen])
  const toggle = useCallback(() => setFullscreen(!isFullscreen), [isFullscreen, setFullscreen])

  return useMemo(() => ({ isFullscreen, enter, exit, toggle }), [isFullscreen, enter, exit, toggle])
}

/**
 * Escape leaves fullscreen — a second way out alongside the toolbar button, matching the
 * site map page's fullscreen behaviour. Mount once, wherever the mode can be entered.
 */
export function useExitFullscreenOnEscape() {
  const { isFullscreen, exit } = useFullscreenModeControls()

  useEffect(() => {
    if (!isFullscreen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') exit()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen, exit])
}
